/**
 * Servicio para gestión de personas.
 * Incluye validación de CUIT único por organización.
 *
 * @example
 * const { supabase } = useSupabase()
 * const resultado = await personasService.verificarCUITDisponible(supabase!, cuit, organizacionId)
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

// Tipo para el resultado de verificación de CUIT
export interface VerificacionCUIT {
  disponible: boolean
  clienteExistente?: {
    id: string
    nombre: string
  }
}

/**
 * Verifica si un CUIT está disponible en una organización.
 * Usa la función de BD verificar_cuit_unico_cliente para hacer el JOIN.
 *
 * @param client - Cliente Supabase autenticado
 * @param cuit - CUIT a verificar (con o sin guiones)
 * @param organizacionId - ID de la organización
 * @param excluirClienteId - ID del cliente a excluir (para modo edición)
 * @returns Objeto con disponibilidad y datos del cliente existente si hay duplicado
 */
export async function verificarCUITDisponible(
  client: SupabaseClient<Database>,
  cuit: string,
  organizacionId: string,
  excluirClienteId?: string
): Promise<VerificacionCUIT> {
  // Limpiar CUIT (quitar guiones y espacios)
  const cuitLimpio = cuit?.replace(/\D/g, '') || ''

  // Si no hay CUIT o no tiene 11 dígitos, está disponible
  if (!cuitLimpio || cuitLimpio.length !== 11) {
    return { disponible: true }
  }

  // Llamar a la función de BD
  const { data, error } = await client.rpc('verificar_cuit_unico_cliente', {
    p_cuit: cuitLimpio,
    p_organizacion_id: organizacionId,
    p_excluir_cliente_id: excluirClienteId,
  })

  if (error) {
    // Distinguir error "función no existe" (backward compat) vs otros errores
    const isMissingFunctionError =
      (typeof error.code === 'string' && error.code === '42883') ||
      (typeof error.message === 'string' &&
        error.message.toLowerCase().includes('verificar_cuit_unico_cliente') &&
        error.message.toLowerCase().includes('does not exist'))

    if (isMissingFunctionError) {
      console.warn(
        'Función verificar_cuit_unico_cliente no existe, omitiendo validación de CUIT:',
        error
      )
      return { disponible: true }
    }

    // Para otros errores, propagarlos para que el backend pueda bloquear
    console.error('Error verificando CUIT:', error)
    throw error
  }

  // La función retorna un array con un registro
  const resultado = Array.isArray(data) ? data[0] : data

  if (!resultado || resultado.disponible) {
    return { disponible: true }
  }

  return {
    disponible: false,
    clienteExistente: {
      id: resultado.cliente_existente_id!,
      nombre: resultado.cliente_existente_nombre || 'Cliente existente',
    },
  }
}

/**
 * True if the CUIT belongs to a client of the organization, even one the caller cannot see.
 * verificarCUITDisponible respects RLS: for an asistente the hidden client reads as available.
 * Returns no id or name. Requires crearCliente in the organization (42501 otherwise).
 */
export async function existeClienteConCuit(
  client: SupabaseClient<Database>,
  cuit: string,
  organizacionId: string
): Promise<boolean> {
  const { data, error } = await client.rpc('existe_cliente_con_cuit', {
    p_cuit: cuit.replace(/\D/g, ''),
    p_organizacion_id: organizacionId,
  })

  if (error) throw error
  return data === true
}

/**
 * Actualiza una persona aplicando un patch parcial.
 * Defensa cross-tenant: requiere organizacionId y filtra por él.
 */
export async function actualizar(
  client: SupabaseClient<Database>,
  id: string,
  patch: Database['public']['Tables']['personas']['Update'],
  organizacionId: string,
): Promise<void> {
  if (!organizacionId) {
    throw new Error('organizacionId requerido para actualizar persona')
  }
  const { error, count } = await client
    .from('personas')
    .update(patch, { count: 'exact' })
    .eq('id', id)
    .eq('organizacion_id', organizacionId)

  if (error) throw error
  if (count === 0) {
    throw new Error(
      'No se actualizó ninguna persona — verificá que pertenezca a tu organización',
    )
  }
}

// -----------------------------------------
// Objeto exportado para compatibilidad
// -----------------------------------------
export const personasService = {
  verificarCUITDisponible,
  existeClienteConCuit,
  actualizar,
}
